import {
    Badge,
    BadgeType,
    DataTable,
    DataTablePresenter,
    formatDate,
    ITableColumn,
    ProviderEvidence,
    TitleCard,
} from '@enter/core';
import { BaseButton } from '@enter/core';
import { mdiInformationOutline } from '@mdi/js';
import Icon from '@mdi/react';
import React, { useMemo } from 'react';
import styled from '@emotion/styled/macro';

const StyledTitleCard = styled(TitleCard)`
    justify-content: space-between;
    max-width: 100%;
`;

const CardBody = styled.div`
    display: flex;
    flex-direction: column;
    flex: 1 1 100%;
`;

const StyledDataTable = styled(DataTable)`
    display: flex;
    flex-direction: column;
    margin: 16px 0;
    flex: 1 1 100%;
    box-shadow: 0 0.5px 0 0 #e5e5e5;

    [role='columnheader'] {
        padding: 8px 12px 6px 12px;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }
    [role='cell'] {
        display: inline-block;
        padding: 8px 12px 6px 12px;
        line-height: 24px;
        font-size: 15px;
        color: #666;

        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }
`;

interface IRequiredEvidenceTableProps {
    data: ProviderEvidence[];
    isLoading?: boolean;
    onViewEvidence?: (evidence: ProviderEvidence) => void;
}

const getStatusBadgeType = (status: string | undefined): BadgeType => {
    switch (status) {
        case "APPROVED":
            return BadgeType.GREEN;
        case "PENDING":
            return BadgeType.ORANGE;
        case "DENIED":
            return BadgeType.RED;
        case "EXPIRED":
            return BadgeType.BLUE_LIGHT;
        default:
            return BadgeType.BLUE_LIGHT;
    }
};

export const RequiredEvidenceTable: React.FC<IRequiredEvidenceTableProps> = ({
    data,
    isLoading = false,
    onViewEvidence,
}) => {
    const columns: ITableColumn<ProviderEvidence>[] = useMemo(() => [
        {
            id: 'status',
            Header: 'Status',
            accessor: 'status',
            Cell: ({ value }) => (
                <Badge type={getStatusBadgeType(value)} size="small">
                    {value || 'N/A'}
                </Badge>
            ),
            minWidth: 100,
        },
        {
            id: 'name',
            Header: 'Name',
            accessor: 'evidenceType',
            Cell: ({ value }) => <span style={{ fontWeight: '500' }}>{value?.name || 'N/A'}</span>,
            minWidth: 150,
        },
        {
            id: 'description',
            Header: 'Description',
            accessor: 'evidenceType',
            Cell: ({ value }) => <span>{value?.description || 'N/A'}</span>,
            minWidth: 200,
        },
        {
            id: 'updated',
            Header: 'Updated',
            accessor: 'updated',
            Cell: ({ value }) => value ? formatDate(value, 'MMM d, yyyy') : 'N/A',
            minWidth: 120,
        },
        {
            id: 'expired',
            Header: 'Expired',
            accessor: 'expired',
            Cell: ({ value }) => value ? formatDate(value, 'MMM d, yyyy') : 'N/A',
            minWidth: 120,
        },
        {
            id: 'created',
            Header: 'Created',
            accessor: 'created',
            Cell: ({ value }) => value ? formatDate(value, 'MMM d, yyyy') : 'N/A',
            minWidth: 120,
        },
        {
            id: 'Action',
            Header: '',
            disableSortBy: true,
            Cell: ({ row }): React.ReactElement => {
                return (
                    <div
                        onClick={(e): void => {
                            e.stopPropagation();
                        }}
                        onMouseDown={(e): void => {
                            e.stopPropagation();
                        }}
                    >
                        <BaseButton
                            variant="text"
                            size="small"
                            onClick={(e): void => {
                                e.preventDefault();
                                e.stopPropagation();
                                onViewEvidence?.(row.original);
                            }}
                        >
                            <Icon path={mdiInformationOutline} size={0.8} style={{ marginRight: '4px' }} />
                            View
                        </BaseButton>
                    </div>
                );
            },
        },
    ], [onViewEvidence]);

    const presenter = useMemo(() => new DataTablePresenter({
        name: 'requiredEvidence',
        data: data,
        columns: columns,
        filterSchemas: [],
    }), [data, columns]);

    return (
        <StyledTitleCard
            size="large"
            title="Required Evidence"
        >
            <CardBody>
                <StyledDataTable
                    presenter={presenter}
                    isLoading={isLoading}
                    disableSorting={false}
                    hideFiltering={true}
                    hideNavigation={true}
                    hidePagination={true}
                    hideSettings={true}
                />
            </CardBody>
        </StyledTitleCard>
    );
};

